import { PropsWithChildren, useCallback } from 'react';

import * as Sentry from '@sentry/react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';

import { CAST_PILOT_ASSISTANT } from '@cast/constants';
import {
  HexIcon,
  Icons,
  LinkBehaviour,
  Placeholder,
  Stack,
} from '@cast/design-system';

import { trackOpsPilot } from './_analytics';
import { ChatHeader } from './ChatHeader';

type ChatErrorBoundaryProps = PropsWithChildren<{
  onClose: () => void;
  onNewChat: () => void;
}>;

export const ChatErrorBoundary = ({
  children,
  onClose,
  onNewChat,
}: ChatErrorBoundaryProps) => {
  const FallbackComponent = useCallback(
    ({ resetErrorBoundary }: FallbackProps) => {
      const handleNewChat = () => {
        resetErrorBoundary();
        onNewChat();
      };
      const handleClose = () => {
        resetErrorBoundary();
        onClose();
      };

      return (
        <Stack flexGrow={1} onClick={resetErrorBoundary}>
          <ChatHeader
            onStartNewChat={handleNewChat}
            onClose={handleClose}
            onToggleHistory={() => null}
            showFullScreenButton={false}
            showHistory={false}
            isFullScreen={true}
            onToggleFullScreen={() => null}
          />
          <Stack flexGrow={1} justifyContent="center">
            <Placeholder
              icon={
                <HexIcon
                  variant="error"
                  size={44}
                  icon={<Icons.Warning weight="fill" />}
                />
              }
              title={`${CAST_PILOT_ASSISTANT} chat has encountered an issue`}
              body={
                <Stack direction="row" flexWrap="wrap" gap={4}>
                  <span>You can either try to </span>
                  <LinkBehaviour component="span" onClick={handleNewChat}>
                    start new chat
                  </LinkBehaviour>
                  <span>or </span>
                  <LinkBehaviour component="span" onClick={handleClose}>
                    close the sidebar
                  </LinkBehaviour>
                </Stack>
              }
            />
          </Stack>
        </Stack>
      );
    },
    [onClose, onNewChat]
  );

  return (
    <ErrorBoundary
      onError={(error) => {
        Sentry.captureException(error);
        trackOpsPilot.chatCrashed({
          error_name: error.name,
          error_message: error.message,
        });
      }}
      FallbackComponent={FallbackComponent}
    >
      {children}
    </ErrorBoundary>
  );
};
